import { Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "@/data";

export function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-[#2c1f0e]/95 backdrop-blur-sm shadow-lg" : "bg-transparent"
      }`}
    >
      <nav className="container mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <span className="text-[#c8a96e] font-serif text-xl tracking-widest uppercase font-semibold">Abhyaas</span>
          <span className="hidden sm:block text-[#c8a96e]/60 text-xs tracking-widest font-light">School of Yoga & Wellness™</span>
        </Link>

        {/* Desktop nav */}
        <ul className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <li key={l.label}>
              <Link
                to={l.href}
                className={`text-sm tracking-wide transition-colors duration-200 ${
                  pathname === l.href
                    ? "text-[#c8a96e] font-semibold"
                    : "text-[#f0e6d3]/80 hover:text-[#c8a96e]"
                }`}
              >
                {l.label}
              </Link>
            </li>
          ))}
          <li>
            <a
              href="https://abhyaas.thinkific.com"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#c8a96e] text-[#2c1f0e] px-5 py-2 rounded-full text-sm font-semibold hover:bg-[#d4b87c] transition-colors duration-200"
            >
              學員登入
            </a>
          </li>
        </ul>

        {/* Mobile toggle */}
        <button
          className="md:hidden text-[#c8a96e] p-2"
          onClick={() => setOpen(!open)}
          aria-label="選單"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {/* Mobile menu */}
      {open && (
        <div className="md:hidden bg-[#2c1f0e]/98 border-t border-[#c8a96e]/20 px-6 py-6">
          <ul className="flex flex-col gap-5">
            {NAV_LINKS.map((l) => (
              <li key={l.label}>
                <Link
                  to={l.href}
                  onClick={() => setOpen(false)}
                  className="text-[#f0e6d3]/90 hover:text-[#c8a96e] text-base tracking-wide block"
                >
                  {l.label}
                </Link>
              </li>
            ))}
            <li>
              <a
                href="https://abhyaas.thinkific.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-[#c8a96e] text-[#2c1f0e] px-6 py-2.5 rounded-full text-sm font-semibold"
              >
                學員登入 Thinkific
              </a>
            </li>
          </ul>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  return (
    <footer className="bg-[#1a1208] text-[#f0e6d3]/70 pt-16 pb-8">
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          <div>
            <div className="text-[#c8a96e] font-serif text-xl tracking-widest uppercase mb-3">Abhyaas</div>
            <div className="text-xs tracking-widest text-[#c8a96e]/60 mb-4">School of Yoga & Wellness™</div>
            <p className="text-sm leading-relaxed">
              持續的練習，是通往智慧的唯一道路。<br />
              Abhyasa — the path of continuous practice.
            </p>
          </div>
          <div>
            <div className="text-[#c8a96e] text-xs tracking-widest uppercase mb-4 font-semibold">課程</div>
            <ul className="space-y-2 text-sm">
              {["RYT200 師資培訓", "RYT300 進階培訓", "YACEP 陰瑜伽", "YACEP 樂齡瑜伽", "YACEP 椅子瑜伽", "YACEP 瑜伽輪", "YACEP 瑜伽經", "YACEP 體式調整", "RPYT 產前瑜伽"].map((c) => (
                <li key={c}><a href="/courses" className="hover:text-[#c8a96e] transition-colors">{c}</a></li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-[#c8a96e] text-xs tracking-widest uppercase mb-4 font-semibold">知識庫</div>
            <ul className="space-y-2 text-sm">
              <li><a href="/knowledge" className="hover:text-[#c8a96e] transition-colors">瑜伽知識庫</a></li>
              <li><a href="/knowledge" className="hover:text-[#c8a96e] transition-colors">瑜伽哲學冷知識</a></li>
              <li><a href="/knowledge" className="hover:text-[#c8a96e] transition-colors">情緒管理與瑜伽</a></li>
            </ul>
          </div>
          <div>
            <div className="text-[#c8a96e] text-xs tracking-widest uppercase mb-4 font-semibold">聯絡</div>
            <ul className="space-y-2 text-sm" id="contact">
              <li>📞 9293-5939</li>
              <li>✉️ abhyaas.hk@gmail.com</li>
              <li className="pt-2">
                <a href="https://instagram.com/abhyaasyoga" target="_blank" rel="noopener noreferrer"
                  className="hover:text-[#c8a96e] transition-colors">Instagram @abhyaasyoga</a>
              </li>
              <li>
                <a href="https://facebook.com/abhyaasyoga" target="_blank" rel="noopener noreferrer"
                  className="hover:text-[#c8a96e] transition-colors">Facebook Abhyaas Yoga</a>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-[#c8a96e]/20 pt-6 text-center text-xs text-[#c8a96e]/40">
          © 2026 Abhyaas - School of Yoga & Wellness™ · Virginia Law · All rights reserved
        </div>
      </div>
    </footer>
  );
}
